import Vue from 'vue'
import VueRouter from 'vue-router'
import Start from '../vcs/start/cmp.vue'
import AbstractRouteCmp from '@/vcs/_shared/AbstractRouteCmp.vue'
import store from '../stores/Store.js'

import HeaderBar from '@/vcs/_shared/header/Header.vue'
import MenuCmp from '@/vcs/_shared/header/MenuCmp.vue'
import LocaleButtons from '@/vcs/_shared/header/LocaleButtons.vue'
import GoBackButton from '@/vcs/_shared/header/GoBackButton.vue'
import GoToStartButton from '@/vcs/_shared/header/GoToStartButton.vue'

import NavigationBar from '@/vcs/_shared/navigation/Navigation.vue'
import DeliveryAuthorization from '@/vcs/_shared/session/DeliveryAuthorization.vue'
import SupportAuthorization from '@/vcs/_shared/session/SupportAuthorization.vue'
import RemoteAuthorization from '@/vcs/_shared/session/RemoteAuthorization.vue'

import IneaInfoModal from '@/vcs/_shared/modals/IneaInfoModal.vue'

// https://stackoverflow.com/a/63263736
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }

  return originalPush.call(this, location).catch((err) => {
    if (VueRouter.isNavigationFailure(err)) {
      return err
    }
    return Promise.reject(err)
  })
}

Vue.use(VueRouter)
const routes = [
  {
    path: '/',
    name: 'Start',
    components: {
      header: HeaderBar,
      content: Start
    },
    meta: {
      headerComponent: {
        left: LocaleButtons,
        right: MenuCmp
      },
      hasBranding: true
    }
  },
  {
    path: '/auth',
    name: 'Auth',
    components: {
      header: HeaderBar,
      content: DeliveryAuthorization
    },
    meta: { headerComponent: { left: GoToStartButton } },
    beforeEnter: (to, from, next) => {
      let comingFromRoot = from.path == '/'

      if (comingFromRoot && isIneaCollecotmate()) {
        showIneaInfoModal()
      }
      next()
    }
  },
  {
    path: '/support_authorization',
    name: 'SupportAuthorization',
    components: {
      header: HeaderBar,
      content: SupportAuthorization
    },
    meta: { headerComponent: { left: GoToStartButton } }
  },
  {
    path: '/qr',
    props: (route) => ({ query: route.query.q }),
    name: 'RemoteAuthorization',
    components: {
      header: HeaderBar,
      content: RemoteAuthorization
    }
  },
  {
    path: '/pickup',
    name: 'Pickup',
    components: {
      header: HeaderBar,
      content: () =>
        import(/* webpackChunkName: "pickup" */ '../vcs/flows/pickup/cmp.vue')
    },
    meta: { headerComponent: { left: GoBackButton } }
  },
  {
    path: '/delivery',
    components: {
      content: AbstractRouteCmp
    },
    beforeEnter: (to, from, next) => {
      let comingFromRoot = from.path == '/'

      if (comingFromRoot && isIneaCollecotmate()) {
        showIneaInfoModal()
      }
      next()
    },
    children: [
      {
        path: '',
        components: {
          header: HeaderBar,
          content: () =>
            import(
              /* webpackChunkName: "delivery-start" */ '../vcs/flows/delivery/step-1-delivery-start/cmp.vue'
            )
        },
        meta: { headerComponent: { left: GoToStartButton } }
      },
      {
        path: 'delivery-request/:id',
        name: 'DeliveryUpdate',
        components: {
          header: HeaderBar,
          content: () =>
            import(
              /* webpackChunkName: "delivery-update" */ '../vcs/flows/delivery/step-2-delivery-update/cmp.vue'
            )
        },
        meta: { headerComponent: { left: GoBackButton } }
      },
      {
        path: 'delivery-request/:id/confirmation',
        name: 'Confirmation',
        components: {
          header: HeaderBar,
          content: () =>
            import(
              /* webpackChunkName: "delivery-confirmation" */ '../vcs/flows/delivery/step-3-delivery-confirmation/cmp.vue'
            )
        },
        meta: { headerComponent: { left: GoBackButton } }
      }
    ]
  },
  {
    path: '/support',
    components: {
      content: AbstractRouteCmp
    },
    beforeEnter: (to, from, next) => {
      if (navigateToSupportFromStartOrQRAuth(to, from)) {
        next({ path: '/support_authorization' })
      } else {
        next()
      }
    },
    children: [
      {
        path: '',
        name: 'SupportDeliveryRequests',
        components: {
          header: HeaderBar,
          content: () =>
            import(
              /* webpackChunkName: "support-delivery-requests" */ '../vcs/flows/support/support-delivery-requests/cmp.vue'
            ),
          navigation: NavigationBar
        },
        meta: { headerComponent: { left: GoToStartButton } }
      },
      {
        path: 'collectomate',
        name: 'SupportCollectomate',
        components: {
          header: HeaderBar,
          content: () =>
            import(
              /* webpackChunkName: "support-collectomate" */ '../vcs/flows/support/support-collectomate/SupportCollectomate.vue'
            ),
          navigation: NavigationBar
        },
        meta: { headerComponent: { left: GoBackButton } }
      },
      {
        path: 'settings',
        name: 'SupportSettings',
        components: {
          header: HeaderBar,
          content: () =>
            import(
              /* webpackChunkName: "support-settings" */ '../vcs/flows/support/support-settings/SupportSettings.vue'
            ),
          navigation: NavigationBar
        },
        meta: { headerComponent: { left: GoBackButton } }
      }
    ]
  },
  {
    path: '/playground',
    name: 'Playground',
    components: {
      content: () =>
        import(/* webpackChunkName: "playground" */ '../vcs/playground/Playground.vue')
    }
  }
]

const router = new VueRouter({
  routes,
  scrollBehavior(to, from, savedPosition) {
    // https://router.vuejs.org/guide/advanced/scroll-behavior.html#async-scrolling
    return { x: 0, y: 0 }
  }
})

const allowedRoutes = [
  '/qr'
  // '/redirected_from_client_app/'
]

const isModalBlockingNavigation = () => {
  // return isAnyModalOpened() && from.name != 'RemoteAuthorization'

  let modals = store.getters['modal/getModals']
  let modalsRedirects = modals.map((modal) => {
    return modal.disableRedirect
  })

  return modalsRedirects.some((el) => !!el)
}

const isIneaCollecotmate = () => {
  let localhost = '72adb1f4-250d-407e-913b-2f24741523e1'
  let ineaPreprod = '4cac86f0-717e-4b09-b0b8-2ce88eae7ec0'
  let ineaProd = '738cc951-724c-422d-b9a5-2de1b8464f32'
  let cp = store.getters.getCurrentCp

  if (cp) {
    return [localhost, ineaPreprod, ineaProd].includes(cp.id)
  } else {
    return false
  }
}

const showIneaInfoModal = () => {
  store.commit('modal/open', {
    cmp: IneaInfoModal,
    size: 'full'
  })
}

const slideDown = () => {
  store.commit('slideUp', 0)
}

const shouldRedirectDeliveryAuth = (to, from) => {
  if (Vue.prototype.$dev) return false

  let hasRestrictedDeliveryAccess = (JSON.parse(window.localStorage.getItem('cp')) || {})
    .hasRestrictedDeliveryAccess
  return to.path == '/delivery' && from.path == '/' && hasRestrictedDeliveryAccess
}

const navigateToSupportFromStartOrQRAuth = (to, from) => {
  return (
    ['/support', '/support/settings'].includes(to.path) &&
    ['/', '/qr'].includes(from.path)
  )
}

const isRedirectedFromClient = (to, from) => {
  return to.path == '/redirected_from_client_app/'
}

const shouldRedirectToRemoteAuth = (to, from) => {
  let isRemoteClient = Vue.prototype.$isRemoteClient
  let userNotLogged = !store.getters.getUserToken.length
  let unallowedRoute = !allowedRoutes.includes(to.path)

  if (isRedirectedFromClient(to, from)) {
    let clientUser = JSON.parse(to?.query?.user)
    console.log('clientUser', clientUser)
    store.commit('setClientUser', clientUser)
  }

  return isRemoteClient && userNotLogged && unallowedRoute
}

router.beforeEach((to, from, next) => {
  if (shouldRedirectToRemoteAuth(to, from)) {
    slideDown()
    next({ path: '/qr' })
  } else if (shouldRedirectDeliveryAuth(to, from)) {
    slideDown()
    next({ path: '/auth' })
  } else if (isModalBlockingNavigation()) {
    next(false)
  } else {
    slideDown()
    next()
  }
})

export default router
